import {
  DEFAULT_FUNDING_PCT,
  DEFAULT_REV_SHARE_PCT,
  MAX_PLAN_MARKETING_MONTHS,
  START_MONTH,
  START_YEAR,
} from './constants';

function parseCalendarMonth(calendarMonth) {
  const match = String(calendarMonth || '').trim().match(/^(\d{4})-(\d{1,2})/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

function addCalendarMonths(year, month, offset) {
  const zeroBased = month - 1 + offset;
  return {
    year: year + Math.floor(zeroBased / 12),
    month: (zeroBased % 12) + 1,
  };
}

function calendarMonthKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function monthsBetweenInclusive(start, end) {
  return (end.year - start.year) * 12 + (end.month - start.month) + 1;
}

/** True when AI row calendar months have holes (e.g. May and Jul but no Jun). */
export function hasCalendarGapsInRows(orderedRows) {
  if (orderedRows.length <= 1) {
    return false;
  }
  const first = orderedRows[0];
  const last = orderedRows[orderedRows.length - 1];
  const span = monthsBetweenInclusive(
    { year: first.year, month: first.month },
    { year: last.year, month: last.month }
  );
  return span > orderedRows.length;
}

function remapRowsToConsecutivePlanStart(orderedRows, planStartYear, planStartMonth) {
  const remapped = new Map();
  for (let i = 0; i < orderedRows.length; i += 1) {
    const { year, month } = addCalendarMonths(planStartYear, planStartMonth, i);
    const key = calendarMonthKey(year, month);
    const row = orderedRows[i];
    remapped.set(key, {
      marketingSpend: row.marketingSpend,
      fundingPct: row.fundingPct,
      revSharePct: row.revSharePct,
      year,
      month,
    });
  }
  return remapped;
}

function currentCalendarMonth() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Build marketing / funding / rev-share arrays from AI marketingPlan extract.
 * By default the first marketing month is the current calendar month (table row 0).
 */
export function buildMarketingPlanFromAssist(
  marketingPlan,
  existingState = {},
  options = {}
) {
  if (!marketingPlan?.applied || !marketingPlan.rows?.length) {
    return null;
  }

  const cohortAnchoredStart = options.cohortAnchoredStart ?? null;
  const firstPlanMonthIsCurrentMonth = cohortAnchoredStart
    ? false
    : marketingPlan.firstPlanMonthIsCurrentMonth !== false;

  const defaultFundingPct = existingState.defaultFundingPct ?? DEFAULT_FUNDING_PCT;
  const defaultRevSharePct = existingState.defaultRevSharePct ?? DEFAULT_REV_SHARE_PCT;

  const rowByMonth = new Map();
  const orderedRows = [];
  const indexFallbackRows = [];
  for (const row of marketingPlan.rows.slice(0, MAX_PLAN_MARKETING_MONTHS)) {
    const parsed = parseCalendarMonth(row.calendarMonth);
    const fundingPct = Number(row.fundingPct);
    const revShareRaw = row.revSharePct;
    const revSharePct =
      revShareRaw != null && Number.isFinite(Number(revShareRaw))
        ? Number(revShareRaw)
        : Number.isFinite(fundingPct)
          ? fundingPct
          : 0;
    const normalized = {
      marketingSpend: Math.max(0, Math.round(Number(row.marketingSpend) || 0)),
      fundingPct: Number.isFinite(fundingPct) ? fundingPct : 0,
      revSharePct,
    };
    if (parsed) {
      const key = calendarMonthKey(parsed.year, parsed.month);
      rowByMonth.set(key, { ...normalized, year: parsed.year, month: parsed.month });
      orderedRows.push({ key, ...normalized, year: parsed.year, month: parsed.month });
    } else if (normalized.marketingSpend > 0 || normalized.fundingPct > 0) {
      indexFallbackRows.push(normalized);
    }
  }

  if (rowByMonth.size === 0 && indexFallbackRows.length > 0) {
    const referenceCurrent = currentCalendarMonth();
    let fallbackStartYear = Number(marketingPlan.planStart?.year);
    let fallbackStartMonth = Number(marketingPlan.planStart?.month);
    if (cohortAnchoredStart) {
      fallbackStartYear = cohortAnchoredStart.year;
      fallbackStartMonth = cohortAnchoredStart.month;
    } else if (!Number.isFinite(fallbackStartYear) || !Number.isFinite(fallbackStartMonth)) {
      if (firstPlanMonthIsCurrentMonth) {
        fallbackStartYear = referenceCurrent.year;
        fallbackStartMonth = referenceCurrent.month;
      } else {
        fallbackStartYear = existingState.planStartYear ?? START_YEAR;
        fallbackStartMonth = (existingState.planStartMonth ?? START_MONTH) + 1;
      }
    }
    for (let i = 0; i < indexFallbackRows.length; i += 1) {
      const { year, month } = addCalendarMonths(fallbackStartYear, fallbackStartMonth, i);
      const key = calendarMonthKey(year, month);
      const row = { ...indexFallbackRows[i], year, month };
      rowByMonth.set(key, row);
      orderedRows.push({ key, ...row });
    }
  }

  if (rowByMonth.size === 0) {
    return null;
  }

  orderedRows.sort((left, right) => left.key.localeCompare(right.key));

  const firstRow = orderedRows[0];
  const lastRow = orderedRows[orderedRows.length - 1];
  const referenceCurrent = currentCalendarMonth();

  let calStartYear = Number(marketingPlan.planStart?.year);
  let calStartMonth = Number(marketingPlan.planStart?.month);
  if (cohortAnchoredStart) {
    calStartYear = cohortAnchoredStart.year;
    calStartMonth = cohortAnchoredStart.month;
  } else if (!Number.isFinite(calStartYear) || !Number.isFinite(calStartMonth)) {
    if (firstPlanMonthIsCurrentMonth) {
      calStartYear = referenceCurrent.year;
      calStartMonth = referenceCurrent.month;
    } else if (firstRow) {
      calStartYear = firstRow.year;
      calStartMonth = firstRow.month;
    } else {
      calStartYear = existingState.planStartYear ?? START_YEAR;
      const storedMonth = existingState.planStartMonth ?? START_MONTH;
      calStartMonth = storedMonth + 1;
    }
  }

  if (firstPlanMonthIsCurrentMonth) {
    calStartYear = referenceCurrent.year;
    calStartMonth = referenceCurrent.month;
  }

  const shouldRemapConsecutiveMonths =
    Boolean(cohortAnchoredStart) || hasCalendarGapsInRows(orderedRows);
  let activeRowByMonth = rowByMonth;
  let planEnd = { year: lastRow.year, month: lastRow.month };

  if (shouldRemapConsecutiveMonths) {
    activeRowByMonth = remapRowsToConsecutivePlanStart(
      orderedRows,
      calStartYear,
      calStartMonth
    );
    planEnd = addCalendarMonths(
      calStartYear,
      calStartMonth,
      orderedRows.length - 1
    );
  } else if (firstPlanMonthIsCurrentMonth) {
    const currentKey = calendarMonthKey(referenceCurrent.year, referenceCurrent.month);
    if (!activeRowByMonth.has(currentKey)) {
      const template = activeRowByMonth.get(orderedRows[0]?.key);
      activeRowByMonth.set(currentKey, {
        marketingSpend: 0,
        fundingPct: 0,
        revSharePct: template?.revSharePct ?? defaultRevSharePct,
        year: referenceCurrent.year,
        month: referenceCurrent.month,
      });
    }
    calStartYear = referenceCurrent.year;
    calStartMonth = referenceCurrent.month;
    planEnd = { year: lastRow.year, month: lastRow.month };
  }

  const planStart = { year: calStartYear, month: calStartMonth };
  let planMonthCount = shouldRemapConsecutiveMonths
    ? orderedRows.length
    : monthsBetweenInclusive(planStart, planEnd);
  planMonthCount = Math.min(
    MAX_PLAN_MARKETING_MONTHS,
    Math.max(orderedRows.length, planMonthCount)
  );

  const totalRows = planMonthCount;
  const marketing = Array(totalRows).fill(0);
  const fundingPct = Array(totalRows).fill(defaultFundingPct);
  const revSharePct = Array(totalRows).fill(defaultRevSharePct);

  for (let i = 0; i < planMonthCount; i += 1) {
    const { year, month } = addCalendarMonths(calStartYear, calStartMonth, i);
    const key = calendarMonthKey(year, month);
    const row = activeRowByMonth.get(key);
    if (row) {
      marketing[i] = row.marketingSpend;
      fundingPct[i] = row.fundingPct;
      revSharePct[i] = row.revSharePct;
    }
  }

  return {
    marketing,
    fundingPct,
    revSharePct,
    planStartYear: calStartYear,
    planStartMonth: calStartMonth - 1,
    firstPlanMonthIsCurrentMonth,
  };
}
